'use client';

import React, { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface QuestTemplate {
  id: string;
  title: string;
  description: string;
  divine_name_resonance: string;
  divine_name_arabic: string;
  divine_name_translation: string;
  caretaking_action_prompt: string;
  urgency: 'ongoing' | 'upcoming' | 'urgent';
  quest_type: 'community' | 'justice' | 'education' | 'environment' | 'worship';
  pillar: 'Keys' | 'Ayat' | 'IRL' | 'MRG';
  linked_divine_names: string[];
  rss_keyword_triggers: string[];
  org_url_patterns: string[];
  is_active: boolean;
  created_at: string;
  use_count: number;
}

interface DivineName {
  id: string;
  arabic: string;
  transliteration: string;
  translation: string;
  resonanceColor: string;
}

// ─── DIVINE NAMES REFERENCE ───────────────────────────────────────────────────

const DIVINE_NAMES_REF: DivineName[] = [
  { id: 'ar-rahman', arabic: 'الرَّحْمَن', transliteration: 'Ar-Rahman', translation: 'The Most Compassionate', resonanceColor: '#2d8a87' },
  { id: 'ar-rahim', arabic: 'الرَّحِيم', transliteration: 'Ar-Rahim', translation: 'The Most Especially Compassionate', resonanceColor: '#2d7a47' },
  { id: 'al-khaliq', arabic: 'الْخَالِق', transliteration: 'Al-Khaliq', translation: 'The Creator', resonanceColor: '#8a8ab8' },
  { id: 'al-alim', arabic: 'الْعَلِيم', transliteration: 'Al-Alim', translation: 'The All-Knowing', resonanceColor: '#D4A82A' },
  { id: 'al-qadir', arabic: 'الْقَادِر', transliteration: 'Al-Qadir', translation: 'The All-Powerful', resonanceColor: '#c9a84c' },
  { id: 'al-haqq', arabic: 'الْحَق', transliteration: 'Al-Haqq', translation: 'The Truth', resonanceColor: '#b8860b' },
  { id: 'al-hakim', arabic: 'الْحَكِيم', transliteration: 'Al-Hakim', translation: 'The Most Wise', resonanceColor: '#4a7c59' },
  { id: 'al-latif', arabic: 'اللَّطِيف', transliteration: 'Al-Latif', translation: 'The Subtle', resonanceColor: '#7a9e7e' },
  { id: 'al-khabir', arabic: 'الْخَبِير', transliteration: 'Al-Khabir', translation: 'The All-Aware', resonanceColor: '#5a7a8a' },
  { id: 'ar-razzaq', arabic: 'الرَّزَّاق', transliteration: 'Ar-Razzaq', translation: 'The Provider', resonanceColor: '#3a7a4a' },
  { id: 'al-wadud', arabic: 'الْوَدُود', transliteration: 'Al-Wadud', translation: 'The Most Loving', resonanceColor: '#c47a7a' },
  { id: 'al-adl', arabic: 'الْعَدْل', transliteration: 'Al-Adl', translation: 'The Just', resonanceColor: '#7a7ac4' },
  { id: 'al-muhyi', arabic: 'الْمُحْيِي', transliteration: 'Al-Muhyi', translation: 'The Giver of Life', resonanceColor: '#4a9a6a' },
  { id: 'al-nur', arabic: 'النُّور', transliteration: 'Al-Nur', translation: 'The Light', resonanceColor: '#c4a84a' },
  { id: 'al-hadi', arabic: 'الْهَادِي', transliteration: 'Al-Hadi', translation: 'The Guide', resonanceColor: '#6a8ab8' },
];

const URGENCY_CONFIG = {
  ongoing: { label: 'Ongoing', color: '#2d7a47', bg: 'rgba(45,122,71,0.15)' },
  upcoming: { label: 'Upcoming', color: '#D4A82A', bg: 'rgba(212,168,42,0.15)' },
  urgent: { label: 'Urgent', color: '#c94a4a', bg: 'rgba(201,74,74,0.15)' },
};

const QUEST_TYPES = ['community', 'justice', 'education', 'environment', 'worship'] as const;
const PILLARS = ['Keys', 'Ayat', 'IRL', 'MRG'] as const;

const PILLAR_COLORS: Record<string, string> = {
  Keys: '#D4A82A', Ayat: '#2d8a87', IRL: '#8a8ab8', MRG: '#2d7a47',
};

// ─── EMPTY TEMPLATE ───────────────────────────────────────────────────────────

const emptyTemplate = (): Omit<QuestTemplate, 'id' | 'created_at' | 'use_count'> => ({
  title: '',
  description: '',
  divine_name_resonance: 'Ar-Rahman',
  divine_name_arabic: 'الرَّحْمَن',
  divine_name_translation: 'The Most Compassionate',
  caretaking_action_prompt: '',
  urgency: 'ongoing',
  quest_type: 'community',
  pillar: 'MRG',
  linked_divine_names: [],
  rss_keyword_triggers: [],
  org_url_patterns: [],
  is_active: true,
});

// ─── MOCK TEMPLATES (pre-seeded for RSS scraper) ─────────────────────────────

const SEED_TEMPLATES: Omit<QuestTemplate, 'id' | 'created_at' | 'use_count'>[] = [
  {
    title: 'Community Iftar & Food Distribution',
    description: 'Organize or volunteer at a community iftar event that provides meals to those in need, reflecting the generosity of Ar-Razzaq.',
    divine_name_resonance: 'Ar-Razzaq',
    divine_name_arabic: 'الرَّزَّاق',
    divine_name_translation: 'The Provider',
    caretaking_action_prompt: 'Identify a local food distribution event and commit to volunteering for at least 2 hours. Document the experience and share one reflection on provision and gratitude.',
    urgency: 'upcoming',
    quest_type: 'community',
    pillar: 'MRG',
    linked_divine_names: ['ar-razzaq', 'ar-rahman', 'al-wadud'],
    rss_keyword_triggers: ['iftar', 'food distribution', 'food bank', 'meal', 'hunger', 'ramadan'],
    org_url_patterns: ['manajustice.org', 'zakat.org', 'icna.org'],
    is_active: true,
  },
  {
    title: 'Environmental Stewardship & Ward Cleanup',
    description: 'Lead or join a neighborhood cleanup initiative, embodying the role of khalifa (steward) of the earth as taught by Al-Khaliq.',
    divine_name_resonance: 'Al-Khaliq',
    divine_name_arabic: 'الْخَالِق',
    divine_name_translation: 'The Creator',
    caretaking_action_prompt: 'Organize a 1-hour ward cleanup in your neighborhood. Photograph before/after. Submit an ecosystem observation noting one natural element you noticed during the cleanup.',
    urgency: 'ongoing',
    quest_type: 'environment',
    pillar: 'IRL',
    linked_divine_names: ['al-khaliq', 'al-muhyi', 'ar-razzaq'],
    rss_keyword_triggers: ['cleanup', 'environment', 'green', 'sustainability', 'park', 'nature', 'stewardship'],
    org_url_patterns: ['ciogc.org', 'manajustice.org'],
    is_active: true,
  },
  {
    title: 'Justice Advocacy & Civic Engagement',
    description: 'Attend or organize a civic engagement event addressing justice issues in your community, guided by Al-Adl (The Just).',
    divine_name_resonance: 'Al-Adl',
    divine_name_arabic: 'الْعَدْل',
    divine_name_translation: 'The Just',
    caretaking_action_prompt: 'Attend one civic meeting, town hall, or advocacy event this month. Write a 3-sentence reflection on what justice looks like in your ward and share it with your community.',
    urgency: 'upcoming',
    quest_type: 'justice',
    pillar: 'MRG',
    linked_divine_names: ['al-adl', 'al-haqq', 'al-hakim'],
    rss_keyword_triggers: ['justice', 'advocacy', 'civic', 'rights', 'policy', 'reform', 'community organizing'],
    org_url_patterns: ['manajustice.org', 'icna.org', 'muslimamericansociety.org'],
    is_active: true,
  },
  {
    title: 'Islamic Education & Knowledge Circle',
    description: 'Facilitate or attend a knowledge circle (halaqah) that deepens understanding of deen and its application to daily life, reflecting Al-Alim.',
    divine_name_resonance: 'Al-Alim',
    divine_name_arabic: 'الْعَلِيم',
    divine_name_translation: 'The All-Knowing',
    caretaking_action_prompt: 'Attend or host one knowledge circle this week. Share one insight you gained with at least 3 people in your network. Document the topic and key takeaway.',
    urgency: 'ongoing',
    quest_type: 'education',
    pillar: 'Keys',
    linked_divine_names: ['al-alim', 'al-hakim', 'al-hadi'],
    rss_keyword_triggers: ['education', 'halaqah', 'class', 'lecture', 'workshop', 'seminar', 'knowledge', 'learning'],
    org_url_patterns: ['icna.org', 'muslimamericansociety.org', 'ciogc.org'],
    is_active: true,
  },
  {
    title: 'Youth Mentorship & Development Program',
    description: 'Engage with youth programs that nurture the next generation of Muslim leaders, embodying the guidance of Al-Hadi.',
    divine_name_resonance: 'Al-Hadi',
    divine_name_arabic: 'الْهَادِي',
    divine_name_translation: 'The Guide',
    caretaking_action_prompt: 'Volunteer as a mentor for one youth program session. Identify one young person in your community who could benefit from guidance and make a commitment to check in with them monthly.',
    urgency: 'ongoing',
    quest_type: 'community',
    pillar: 'Keys',
    linked_divine_names: ['al-hadi', 'al-latif', 'ar-rahim'],
    rss_keyword_triggers: ['youth', 'mentorship', 'young', 'teen', 'student', 'camp', 'program'],
    org_url_patterns: ['manajustice.org', 'muslimamericansociety.org', 'icna.org'],
    is_active: true,
  },
];

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function QuestTemplateAdmin() {
  const [templates, setTemplates] = useState<QuestTemplate[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<Partial<QuestTemplate> | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState<'list' | 'create' | 'seed'>('list');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterUrgency, setFilterUrgency] = useState<string>('all');
  const [keywordInput, setKeywordInput] = useState('');
  const [urlPatternInput, setUrlPatternInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  const supabase = createClient();

  // Load templates from Supabase (or use seed data if table empty)
  useEffect(() => {
    loadTemplates();
  }, []);

  async function loadTemplates() {
    const { data, error } = await supabase
      .from('quest_templates')
      .select('*')
      .order('created_at', { ascending: false });

    if (data && data.length > 0) {
      setTemplates(data);
    } else {
      // Show seed templates as preview
      const seeded = SEED_TEMPLATES.map((t, i) => ({
        ...t,
        id: `seed-${i}`,
        created_at: new Date().toISOString(),
        use_count: 0,
      }));
      setTemplates(seeded);
    }
  }

  async function saveTemplate(template: Partial<QuestTemplate>) {
    setSaving(true);
    setSaveMsg('');

    const payload = {
      title: template.title,
      description: template.description,
      divine_name_resonance: template.divine_name_resonance,
      divine_name_arabic: template.divine_name_arabic,
      divine_name_translation: template.divine_name_translation,
      caretaking_action_prompt: template.caretaking_action_prompt,
      urgency: template.urgency,
      quest_type: template.quest_type,
      pillar: template.pillar,
      linked_divine_names: template.linked_divine_names || [],
      rss_keyword_triggers: template.rss_keyword_triggers || [],
      org_url_patterns: template.org_url_patterns || [],
      is_active: template.is_active ?? true,
    };

    if (template.id && !template.id.startsWith('seed-')) {
      const { error } = await supabase
        .from('quest_templates')
        .update(payload)
        .eq('id', template.id);
      if (!error) setSaveMsg('Template updated ✓');
    } else {
      const { error } = await supabase
        .from('quest_templates')
        .insert(payload);
      if (!error) setSaveMsg('Template created ✓');
    }

    setSaving(false);
    setEditingTemplate(null);
    setIsCreating(false);
    setActiveTab('list');
    loadTemplates();
    setTimeout(() => setSaveMsg(''), 3000);
  }

  async function seedAllTemplates() {
    setSaving(true);
    for (const t of SEED_TEMPLATES) {
      await supabase.from('quest_templates').upsert({
        ...t,
        use_count: 0,
      });
    }
    setSaving(false);
    setSaveMsg('All seed templates pushed to Supabase ✓');
    loadTemplates();
    setTimeout(() => setSaveMsg(''), 3000);
  }

  async function toggleActive(id: string, current: boolean) {
    if (id.startsWith('seed-')) return;
    await supabase.from('quest_templates').update({ is_active: !current }).eq('id', id);
    loadTemplates();
  }

  const filteredTemplates = templates.filter(t => {
    if (filterType !== 'all' && t.quest_type !== filterType) return false;
    if (filterUrgency !== 'all' && t.urgency !== filterUrgency) return false;
    return true;
  });

  const form = editingTemplate || (isCreating ? emptyTemplate() : null);

  function updateForm(field: string, value: unknown) {
    setEditingTemplate(prev => ({ ...(prev || emptyTemplate()), [field]: value }));
  }

  function addKeyword() {
    if (!keywordInput.trim()) return;
    const current = (form?.rss_keyword_triggers || []);
    updateForm('rss_keyword_triggers', [...current, keywordInput.trim().toLowerCase()]);
    setKeywordInput('');
  }

  function addUrlPattern() {
    if (!urlPatternInput.trim()) return;
    const current = (form?.org_url_patterns || []);
    updateForm('org_url_patterns', [...current, urlPatternInput.trim().toLowerCase()]);
    setUrlPatternInput('');
  }

  function selectDivineName(nameId: string) {
    const name = DIVINE_NAMES_REF.find(n => n.id === nameId);
    if (!name) return;
    updateForm('divine_name_resonance', name.transliteration);
    updateForm('divine_name_arabic', name.arabic);
    updateForm('divine_name_translation', name.translation);
  }

  function toggleLinkedName(nameId: string) {
    const current = form?.linked_divine_names || [];
    if (current.includes(nameId)) {
      updateForm('linked_divine_names', current.filter(n => n !== nameId));
    } else {
      updateForm('linked_divine_names', [...current, nameId]);
    }
  }

  // ─── RENDER ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ background: '#060d12', minHeight: '100vh', fontFamily: '"DM Sans", sans-serif', color: '#e8e0d0' }}>
      {/* Header */}
      <div style={{ borderBottom: '1px solid rgba(212,168,42,0.2)', padding: '24px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontFamily: '"Amiri", serif', fontSize: 28, color: '#D4A82A', margin: 0 }}>
            Asbab Quest Template Admin
          </h1>
          <p style={{ color: '#8a9a8a', fontSize: 13, margin: '4px 0 0' }}>
            Reusable quest templates with Divine Name resonance — ready for RSS scraper population
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {saveMsg && (
            <span style={{ color: '#2d7a47', fontSize: 13, background: 'rgba(45,122,71,0.15)', padding: '6px 12px', borderRadius: 6 }}>
              {saveMsg}
            </span>
          )}
          <button
            onClick={() => { setIsCreating(true); setEditingTemplate(emptyTemplate() as QuestTemplate); setActiveTab('create'); }}
            style={{ background: '#D4A82A', color: '#060d12', border: 'none', padding: '10px 20px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          >
            + New Template
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '0 32px' }}>
        {(['list', 'create', 'seed'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); if (tab === 'create') { setIsCreating(true); setEditingTemplate(emptyTemplate() as QuestTemplate); } }}
            style={{
              background: 'none', border: 'none', padding: '14px 20px', cursor: 'pointer',
              color: activeTab === tab ? '#D4A82A' : '#6a7a6a',
              borderBottom: activeTab === tab ? '2px solid #D4A82A' : '2px solid transparent',
              fontSize: 14, fontWeight: activeTab === tab ? 600 : 400, textTransform: 'capitalize',
            }}
          >
            {tab === 'list' ? `Templates (${templates.length})` : tab === 'seed' ? 'Seed Templates' : 'Create New'}
          </button>
        ))}
      </div>

      <div style={{ padding: '24px 32px' }}>

        {/* ── LIST TAB ── */}
        {activeTab === 'list' && (
          <div>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
              <select
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                style={{ background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}
              >
                <option value="all">All Types</option>
                {QUEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <select
                value={filterUrgency}
                onChange={e => setFilterUrgency(e.target.value)}
                style={{ background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}
              >
                <option value="all">All Urgency</option>
                <option value="ongoing">Ongoing</option>
                <option value="upcoming">Upcoming</option>
                <option value="urgent">Urgent</option>
              </select>
              <div style={{ marginLeft: 'auto', color: '#6a7a6a', fontSize: 13, alignSelf: 'center' }}>
                {filteredTemplates.length} templates
              </div>
            </div>

            {/* Template Cards */}
            <div style={{ display: 'grid', gap: 16 }}>
              {filteredTemplates.map(template => {
                const urgencyConf = URGENCY_CONFIG[template.urgency];
                const pillarColor = PILLAR_COLORS[template.pillar] || '#D4A82A';
                const primaryName = DIVINE_NAMES_REF.find(n => n.transliteration === template.divine_name_resonance);

                return (
                  <div
                    key={template.id}
                    style={{
                      background: '#0d1a14', border: '1px solid rgba(212,168,42,0.15)',
                      borderRadius: 12, padding: '20px 24px',
                      borderLeft: `4px solid ${pillarColor}`,
                      opacity: template.is_active ? 1 : 0.5,
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                          <span style={{ background: urgencyConf.bg, color: urgencyConf.color, fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 600 }}>
                            {urgencyConf.label}
                          </span>
                          <span style={{ background: `${pillarColor}22`, color: pillarColor, fontSize: 11, padding: '3px 8px', borderRadius: 4, fontWeight: 600 }}>
                            {template.pillar}
                          </span>
                          <span style={{ background: 'rgba(138,138,184,0.15)', color: '#8a8ab8', fontSize: 11, padding: '3px 8px', borderRadius: 4 }}>
                            {template.quest_type}
                          </span>
                          {template.id.startsWith('seed-') && (
                            <span style={{ background: 'rgba(212,168,42,0.1)', color: '#D4A82A', fontSize: 11, padding: '3px 8px', borderRadius: 4 }}>
                              seed
                            </span>
                          )}
                        </div>
                        <h3 style={{ fontFamily: '"Amiri", serif', fontSize: 20, color: '#e8e0d0', margin: '0 0 8px' }}>
                          {template.title}
                        </h3>
                        <p style={{ color: '#8a9a8a', fontSize: 13, margin: '0 0 12px', lineHeight: 1.6 }}>
                          {template.description}
                        </p>

                        {/* Divine Name */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                          <div style={{
                            background: primaryName ? `${primaryName.resonanceColor}22` : 'rgba(212,168,42,0.1)',
                            border: `1px solid ${primaryName?.resonanceColor || '#D4A82A'}44`,
                            borderRadius: 8, padding: '6px 12px', display: 'flex', gap: 8, alignItems: 'center',
                          }}>
                            <span style={{ fontFamily: '"Amiri", serif', fontSize: 18, color: primaryName?.resonanceColor || '#D4A82A' }}>
                              {template.divine_name_arabic}
                            </span>
                            <span style={{ color: '#8a9a8a', fontSize: 12 }}>
                              {template.divine_name_resonance} · {template.divine_name_translation}
                            </span>
                          </div>
                        </div>

                        {/* Caretaking Action */}
                        <div style={{ background: 'rgba(45,138,135,0.08)', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 10 }}>
                          <div style={{ fontSize: 11, color: '#2d8a87', fontWeight: 600, marginBottom: 4 }}>CARETAKING ACTION</div>
                          <p style={{ color: '#c8d8c8', fontSize: 13, margin: 0, lineHeight: 1.6 }}>
                            {template.caretaking_action_prompt}
                          </p>
                        </div>

                        {/* Keywords & URL Patterns */}
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                          {template.rss_keyword_triggers?.length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, color: '#6a7a6a', marginBottom: 4 }}>RSS KEYWORDS</div>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {template.rss_keyword_triggers.map(kw => (
                                  <span key={kw} style={{ background: 'rgba(255,255,255,0.05)', color: '#8a9a8a', fontSize: 11, padding: '2px 7px', borderRadius: 4 }}>
                                    {kw}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {template.org_url_patterns?.length > 0 && (
                            <div>
                              <div style={{ fontSize: 11, color: '#6a7a6a', marginBottom: 4 }}>ORG URLS</div>
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                {template.org_url_patterns.map(url => (
                                  <span key={url} style={{ background: 'rgba(45,122,71,0.1)', color: '#4a9a6a', fontSize: 11, padding: '2px 7px', borderRadius: 4 }}>
                                    {url}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 100 }}>
                        <button
                          onClick={() => { setEditingTemplate(template); setActiveTab('create'); }}
                          style={{ background: 'rgba(212,168,42,0.1)', border: '1px solid rgba(212,168,42,0.3)', color: '#D4A82A', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                        >
                          Edit
                        </button>
                        {!template.id.startsWith('seed-') && (
                          <button
                            onClick={() => toggleActive(template.id, template.is_active)}
                            style={{ background: template.is_active ? 'rgba(201,74,74,0.1)' : 'rgba(45,122,71,0.1)', border: `1px solid ${template.is_active ? 'rgba(201,74,74,0.3)' : 'rgba(45,122,71,0.3)'}`, color: template.is_active ? '#c94a4a' : '#2d7a47', padding: '7px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                          >
                            {template.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                        <div style={{ textAlign: 'center', fontSize: 11, color: '#6a7a6a', marginTop: 4 }}>
                          Used: {template.use_count || 0}×
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── CREATE/EDIT TAB ── */}
        {activeTab === 'create' && form && (
          <div style={{ maxWidth: 800 }}>
            <h2 style={{ fontFamily: '"Amiri", serif', fontSize: 22, color: '#D4A82A', marginBottom: 24 }}>
              {editingTemplate?.id && !editingTemplate.id.startsWith('seed-') ? 'Edit Template' : 'Create New Template'}
            </h2>

            <div style={{ display: 'grid', gap: 20 }}>
              {/* Title */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>QUEST TITLE *</label>
                <input
                  value={form.title || ''}
                  onChange={e => updateForm('title', e.target.value)}
                  placeholder="e.g. Community Iftar & Food Distribution"
                  style={{ width: '100%', background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '10px 14px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                />
              </div>

              {/* Description */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>DESCRIPTION *</label>
                <textarea
                  value={form.description || ''}
                  onChange={e => updateForm('description', e.target.value)}
                  rows={3}
                  placeholder="Brief description of this quest type and its spiritual significance..."
                  style={{ width: '100%', background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '10px 14px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              {/* Type / Urgency / Pillar */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>QUEST TYPE</label>
                  <select
                    value={form.quest_type || 'community'}
                    onChange={e => updateForm('quest_type', e.target.value)}
                    style={{ width: '100%', background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '10px 14px', borderRadius: 8, fontSize: 14 }}
                  >
                    {QUEST_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>URGENCY</label>
                  <select
                    value={form.urgency || 'ongoing'}
                    onChange={e => updateForm('urgency', e.target.value)}
                    style={{ width: '100%', background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '10px 14px', borderRadius: 8, fontSize: 14 }}
                  >
                    <option value="ongoing">Ongoing</option>
                    <option value="upcoming">Upcoming</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>PILLAR</label>
                  <select
                    value={form.pillar || 'MRG'}
                    onChange={e => updateForm('pillar', e.target.value)}
                    style={{ width: '100%', background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '10px 14px', borderRadius: 8, fontSize: 14 }}
                  >
                    {PILLARS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              {/* Primary Divine Name */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 8, fontWeight: 600 }}>PRIMARY DIVINE NAME RESONANCE</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                  {DIVINE_NAMES_REF.map(name => {
                    const isSelected = form.divine_name_resonance === name.transliteration;
                    return (
                      <button
                        key={name.id}
                        onClick={() => selectDivineName(name.id)}
                        style={{
                          background: isSelected ? `${name.resonanceColor}22` : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${isSelected ? name.resonanceColor : 'rgba(255,255,255,0.08)'}`,
                          borderRadius: 8, padding: '8px 12px', cursor: 'pointer', textAlign: 'left',
                        }}
                      >
                        <div style={{ fontFamily: '"Amiri", serif', fontSize: 16, color: name.resonanceColor }}>{name.arabic}</div>
                        <div style={{ fontSize: 11, color: '#8a9a8a', marginTop: 2 }}>{name.transliteration}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Linked Divine Names */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 8, fontWeight: 600 }}>LINKED DIVINE NAMES TILES (multi-select)</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {DIVINE_NAMES_REF.map(name => {
                    const isLinked = (form.linked_divine_names || []).includes(name.id);
                    return (
                      <button
                        key={name.id}
                        onClick={() => toggleLinkedName(name.id)}
                        style={{
                          background: isLinked ? `${name.resonanceColor}22` : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${isLinked ? name.resonanceColor : 'rgba(255,255,255,0.08)'}`,
                          borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 12,
                          color: isLinked ? name.resonanceColor : '#6a7a6a',
                        }}
                      >
                        {name.transliteration}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Caretaking Action Prompt */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>CARETAKING ACTION PROMPT *</label>
                <textarea
                  value={form.caretaking_action_prompt || ''}
                  onChange={e => updateForm('caretaking_action_prompt', e.target.value)}
                  rows={3}
                  placeholder="Specific, actionable step a caretaker can take. Be concrete and measurable..."
                  style={{ width: '100%', background: '#0d1a14', border: '1px solid rgba(45,138,135,0.3)', color: '#e8e0d0', padding: '10px 14px', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>

              {/* RSS Keyword Triggers */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>RSS KEYWORD TRIGGERS</label>
                <p style={{ color: '#6a7a6a', fontSize: 12, margin: '0 0 8px' }}>
                  Keywords that trigger this template when found in RSS feed titles/descriptions
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    value={keywordInput}
                    onChange={e => setKeywordInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addKeyword()}
                    placeholder="Add keyword (press Enter)"
                    style={{ flex: 1, background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}
                  />
                  <button onClick={addKeyword} style={{ background: 'rgba(212,168,42,0.1)', border: '1px solid rgba(212,168,42,0.3)', color: '#D4A82A', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(form.rss_keyword_triggers || []).map(kw => (
                    <span
                      key={kw}
                      onClick={() => updateForm('rss_keyword_triggers', (form.rss_keyword_triggers || []).filter(k => k !== kw))}
                      style={{ background: 'rgba(255,255,255,0.05)', color: '#8a9a8a', fontSize: 12, padding: '3px 8px', borderRadius: 4, cursor: 'pointer', display: 'flex', gap: 4, alignItems: 'center' }}
                    >
                      {kw} <span style={{ color: '#c94a4a' }}>×</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Org URL Patterns */}
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#8a9a8a', marginBottom: 6, fontWeight: 600 }}>ORG URL PATTERNS</label>
                <p style={{ color: '#6a7a6a', fontSize: 12, margin: '0 0 8px' }}>
                  Org domains this template applies to (e.g. manajustice.org)
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    value={urlPatternInput}
                    onChange={e => setUrlPatternInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addUrlPattern()}
                    placeholder="Add domain (press Enter)"
                    style={{ flex: 1, background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', color: '#e8e0d0', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}
                  />
                  <button onClick={addUrlPattern} style={{ background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)', color: '#2d7a47', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(form.org_url_patterns || []).map(url => (
                    <span
                      key={url}
                      onClick={() => updateForm('org_url_patterns', (form.org_url_patterns || []).filter(u => u !== url))}
                      style={{ background: 'rgba(45,122,71,0.1)', color: '#4a9a6a', fontSize: 12, padding: '3px 8px', borderRadius: 4, cursor: 'pointer', display: 'flex', gap: 4, alignItems: 'center' }}
                    >
                      {url} <span style={{ color: '#c94a4a' }}>×</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Active Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontSize: 12, color: '#8a9a8a', fontWeight: 600 }}>ACTIVE (available for RSS scraper)</label>
                <button
                  onClick={() => updateForm('is_active', !form.is_active)}
                  style={{
                    background: form.is_active ? 'rgba(45,122,71,0.2)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${form.is_active ? '#2d7a47' : 'rgba(255,255,255,0.1)'}`,
                    color: form.is_active ? '#2d7a47' : '#6a7a6a',
                    padding: '6px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  }}
                >
                  {form.is_active ? '✓ Active' : 'Inactive'}
                </button>
              </div>

              {/* Save Button */}
              <div style={{ display: 'flex', gap: 12, paddingTop: 8 }}>
                <button
                  onClick={() => saveTemplate(form)}
                  disabled={saving || !form.title || !form.caretaking_action_prompt}
                  style={{
                    background: saving ? 'rgba(212,168,42,0.3)' : '#D4A82A',
                    color: '#060d12', border: 'none', padding: '12px 28px', borderRadius: 8,
                    fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 15,
                  }}
                >
                  {saving ? 'Saving…' : 'Save Template'}
                </button>
                <button
                  onClick={() => { setEditingTemplate(null); setIsCreating(false); setActiveTab('list'); }}
                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: '#8a9a8a', padding: '12px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 15 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── SEED TAB ── */}
        {activeTab === 'seed' && (
          <div style={{ maxWidth: 700 }}>
            <div style={{ background: '#0d1a14', border: '1px solid rgba(212,168,42,0.2)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
              <h2 style={{ fontFamily: '"Amiri", serif', fontSize: 22, color: '#D4A82A', margin: '0 0 12px' }}>
                Seed Templates to Supabase
              </h2>
              <p style={{ color: '#8a9a8a', fontSize: 14, lineHeight: 1.7, margin: '0 0 16px' }}>
                Push all {SEED_TEMPLATES.length} pre-built quest templates to the Supabase <code style={{ color: '#D4A82A' }}>quest_templates</code> table.
                These templates are tuned to match RSS feed content from CIOGC, Zakat Foundation, ICNA, MAS, and MANA.
                The RSS scraper Edge Function will use these templates to auto-generate Asbab Quest items.
              </p>
              <div style={{ display: 'grid', gap: 8, marginBottom: 20 }}>
                {SEED_TEMPLATES.map((t, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
                    <span style={{ color: PILLAR_COLORS[t.pillar], fontSize: 11, fontWeight: 600, minWidth: 30 }}>{t.pillar}</span>
                    <span style={{ color: '#e8e0d0', fontSize: 13 }}>{t.title}</span>
                    <span style={{ marginLeft: 'auto', color: '#6a7a6a', fontSize: 11 }}>{t.quest_type}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={seedAllTemplates}
                disabled={saving}
                style={{ background: saving ? 'rgba(212,168,42,0.3)' : '#D4A82A', color: '#060d12', border: 'none', padding: '12px 28px', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 15 }}
              >
                {saving ? 'Pushing to Supabase…' : `Push ${SEED_TEMPLATES.length} Templates to Supabase`}
              </button>
              {saveMsg && <p style={{ color: '#2d7a47', marginTop: 12, fontSize: 13 }}>{saveMsg}</p>}
            </div>

            <div style={{ background: '#0d1a14', border: '1px solid rgba(45,138,135,0.2)', borderRadius: 12, padding: 24 }}>
              <h3 style={{ color: '#2d8a87', fontSize: 16, margin: '0 0 12px' }}>RSS Scraper Integration</h3>
              <p style={{ color: '#8a9a8a', fontSize: 13, lineHeight: 1.7, margin: 0 }}>
                The <code style={{ color: '#D4A82A' }}>asbab-rss-feed</code> Edge Function will match incoming RSS items against these templates using keyword triggers and org URL patterns.
                When a match is found, the template's Divine Name resonance, caretaking action, urgency level, and pillar are applied to the auto-generated Asbab Quest item in the <code style={{ color: '#D4A82A' }}>fisabilillah_feed</code> table.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
